import React, { useEffect, useState } from "react"
import _ from 'lodash'
// Data
import assetService from "data/asset-list"
import { canadianProvinces, usStates } from "data/lib/statesAndProvinces"
import validationService from "data/validate"
import { field_attrs } from "components/table/lib/column-field"
// Inputs
import { Dropdown_s, InputText_s, InputNumber_s } from 'styles/input-styles'
import { TextInput, DropdownInput } from 'components/table/carousel/inputs'
import NumberInput from "components/inputs/number"
import { DateText_s, AssetSettings_s } from "components/asset-management/asset-list/asset-list-styles"
// Styles
import { FormSection, FormRow, SectionFormWell } from "styles/form-styles"

const AssetSettings = (props: { asset:object, charts: object[], readonly: boolean }) => {

  const [data, setData] = useState<object>(props.asset);
  const [_errors, setErrors] = useState<object>({})
  const chartOptions = props.charts.map((i) => {
    const label = _.get(i,'name') ? `${_.get(i, "code")} - ${_.get(i, "name")}` : `${_.get(i, "code")}`
    return {label, value: _.get(i, "chartid")}
  })
  const readonly = props.readonly

  useEffect(() => {
    const validationSubscription = validationService.stream().subscribe(setErrors)
    validationService.fetch('asset', props.asset['assetid'])
    validationService.fetch('assetattribute', props.asset['assetid'])

    return function cleanup(){
      validationSubscription.unsubscribe()
    }
  }, [props.asset])

  const persist = (key, val) => {
    let nextData = _.cloneDeep(data)
    _.set(nextData, key, val)
    assetService.patch(nextData);
    setData(nextData);
  }

  useEffect(() => {
    const firstInput = document.getElementById('codeInput')
    firstInput?.focus();
  }, [])

  useEffect(() => {
    setData(props.asset)
  }, [props.asset])

  const attrs = (field) => field_attrs('asset')(field)(props['asset'], props)

  return (
    <AssetSettings_s>
      <FormSection>
        <FormRow>
          <div>
            <label htmlFor="codeInput">
              Code
            </label>
            <TextInput
              className="automated"
              data={data}
              field={'code'}
              persist={e => persist('code', e)}
              attrs={attrs}
              disabled={readonly}
            />
          </div>
          <div>
            <label htmlFor="nameInput">
              Name
            </label>
            <TextInput
              className="automated"
              data={data}
              field={'name'}
              persist={e => persist('name', e)}
              attrs={attrs}
              disabled={readonly}
            />
          </div>
          <div>
            <label htmlFor="propertyTypeDropdown">
              Property Type
            </label>
            <DropdownInput
              className="automated"
              data={data}
              field={'propertytype'}
              persist={e => persist('propertytype', e)}
              attrs={attrs}
              options={['Commercial', 'Multifamily', 'Hospitality'].map(propType => { return { label: propType, value: propType }})}
              disabled={readonly}
            />
          </div>
          <div>
            <label htmlFor="chartDropdown">
              Chart
            </label>
            <DropdownInput
              className="automated"
              data={data}
              field={'chartid'}
              persist={e => persist('chartid', e)}
              attrs={attrs}
              options={chartOptions}
              disabled={readonly}
            />
          </div>
        </FormRow>
        <SectionFormWell>
          <FormRow>
            <div>
              <label htmlFor="countryDropdown">
                Country
              </label>
              <DropdownInput
                className="automated"
                data={data}
                field={'country'}
                persist={e => persist('country', e)}
                attrs={attrs}
                options={['US', 'CA'].map(country => { return { label: country, value: country }})}
                disabled={readonly}
              />
            </div>
            <div>
              <label htmlFor="addressInput">
                Address
              </label>
              <TextInput
                className="automated"
                data={data}
                field={'address'}
                persist={e => persist('address', e)}
                attrs={attrs}
                disabled={readonly}
              />
            </div>
            <div>
              <label htmlFor="cityInput">
                City
              </label>
              <TextInput
                className="automated"
                data={data}
                field={'city'}
                persist={e => persist('city', e)}
                attrs={attrs}
                disabled={readonly}
              />
            </div>
            <div>
              <label htmlFor="stateDropdown">
                State/Province
              </label>
              <DropdownInput
                className="automated"
                data={data}
                field={'state'}
                persist={e => persist('state', e)}
                attrs={attrs}
                options={data['country'] === 'CA' ? canadianProvinces : usStates}
                disabled={readonly}
              />
            </div>
            <div>
              <label htmlFor="postalCodeInput">
                Postal Code
              </label>
              <TextInput
                className="automated"
                data={data}
                field={'postalcode'}
                persist={e => persist('postalcode', e)}
                attrs={attrs}
                disabled={readonly}
              />
            </div>
          </FormRow>
        </SectionFormWell>
        <FormRow>
          <div>
            <label htmlFor="statusDropdown">
              Status
            </label>
            <DropdownInput
              className="automated"
              data={data}
              field={'active'}
              persist={e => persist('active', e)}
              attrs={attrs}
              options={[{label: 'Active', value: true}, {label: 'Inactive', value: false}]}
              disabled={readonly}
            />
          </div>
          <div>
            <label htmlFor="acquisitionDateInput">
              Acquisition Date
            </label>
            <DateText_s
              id="acquisitionDateInput"
              value={data['acquisitiondate']}
              persist={(val) => persist('acquisitiondate', val)}
              disabled={readonly}
            />
          </div>
          <div>
            <label htmlFor="acquisitionAmtInput">
              Acquisition Amt
            </label>
            <InputNumber_s
              id="acquisitionAmtInput"
              inputMode="decimal"
              value={data['acquisitionamt']}
              onChange={e => persist('acquisitionamt', e.value)}
              disabled={readonly}
            />
          </div>
          <div>
            <label htmlFor="dispositionDateInput">
              Disposition Date
            </label>
            <DateText_s
              id="dispositionDateInput"
              value={data['dispositiondate']}
              persist={(val) => persist('dispositiondate', val)}
              disabled={readonly}
            />
          </div>
          <div>
          <label htmlFor="dispositionAmtInput">
              Disposition Amt
            </label>
            <InputNumber_s
              id="dispositionAmtInput"
              value={data['dispositionamt']}
              onChange={e => persist('dispositionamt', e.value)}
              disabled={readonly}
            />
          </div>
        </FormRow>
        <SectionFormWell>
          <FormRow>
            <div>
              <label htmlFor="currencyInput">
                Currency
              </label>
              <Dropdown_s
                id="currencyInput"
                value={data['currency']}
                onChange={e => persist('currency', e.target.value)}
                options={[{label: 'USD', value: 'USD'}]}
                disabled={readonly}
              />
            </div>
            <div>
              <label htmlFor="areaMetricInput">
                Area Metric
              </label>
              <Dropdown_s
                id="areaMetricInput"
                value={data['areametric']}
                onChange={e => persist('areametric', e.target.value)}
                options={[{label: 'Square Foot', value: 'sqft'}]}
                disabled={readonly}
              />
            </div>
            <div>
              <label htmlFor="ownershipPercentInput">
                Ownership %
              </label>
              <NumberInput
                readPrecision={4}
                writePrecision={4}
                value={data['ownerpercent']}
                persist={(field, val) => persist(field, val)}
                field="ownerpercent"
                disabled={readonly}
              />
            </div>
            <div>
              <label htmlFor="yearBuiltInput">
                Year Built
              </label>
              <TextInput
                className="automated"
                data={data}
                field="yearbuilt"
                onChange={(e) => persist('yearbuilt', e.value)}
                attrs={attrs}
                disabled={readonly}
              />
            </div>
            <div>
              <label htmlFor="yearRenovatedInput">
                Year Renovated
              </label>
              <TextInput
                className="automated"
                data={data}
                field="yearrenovated"
                onChange={e => persist('yearrenovated', e.value)}
                attrs={attrs}
                disabled={readonly}
              />
            </div>
          </FormRow>
        </SectionFormWell>
        <FormRow>
          <div>
            <label>
              Notes
            </label>
            <textarea rows={10} cols={110}
              value={data['notedetail'] ? data['notedetail']['notes'] : ''}
              onChange={(e) => persist('notedetail',  {notes: e.target['value']})}
              spellCheck={true}
              disabled={readonly}
            />
          </div>
        </FormRow>
      </FormSection>
    </AssetSettings_s>
  )
}

export default AssetSettings